import {useContext, useEffect, useRef, useState} from "react";
import OpenWeatherMapContext from "../../contexts/OpenWeatherMapContext";
import LoadingSpinner from "../Common/LoadingSpinner";

// widget types, see https://openweathermap.org/widgets-constructor
const OPEN_WEATHER_MAP_WIDGET_TYPE_DESKTOP = 11; // Large, 8-Day Forecast
const OPEN_WEATHER_MAP_WIDGET_TYPE_MOBILE = 12; // Medium Card

// matches the mobile check used elsewhere in this codebase (e.g. Units.js, Alerts.js)
// and the tailwind.config.js 'lg' screen
const MOBILE_WIDTH_MEDIA_QUERY = "(max-width: 1024px)";

const OPEN_WEATHER_MAP_D3_SCRIPT_SRC = "//openweathermap.org/themes/openweathermap/assets/vendor/owm/js/d3.min.js";
const OPEN_WEATHER_MAP_WIDGET_SCRIPT_SRC = "//openweathermap.org/themes/openweathermap/assets/vendor/owm/js/weather-widget-generator.js";

const insertScript = ({
  parentNode,
  src,
  id,
  callback
}) => {
  const script = document.createElement("script");
  if (id) {
    script.id = id;
  }
  script.async = true;
  script.src = src;
  if (callback) {
    script.onload = callback;
  }
  parentNode.appendChild(script);
  return script;
}

const OpenWeatherMapWeatherWidget = ({selectedFleet, "data-testid": dataTestId = "openweathermap-widget"}) => {
  const {apiKey} = useContext(OpenWeatherMapContext);
  const cityId = selectedFleet?.weatherWidgetId;
  const widgetRef = useRef(null);
  const widgetScriptRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_WIDTH_MEDIA_QUERY).matches
  );
  const widgetType = isMobile ? OPEN_WEATHER_MAP_WIDGET_TYPE_MOBILE : OPEN_WEATHER_MAP_WIDGET_TYPE_DESKTOP;
  const containerId = `openweathermap-widget-${cityId}-${widgetType}`

  useEffect(() => {
    const mobileWidthMediaQueryList = window.matchMedia(MOBILE_WIDTH_MEDIA_QUERY);
    const handleMobileWidthChange = (event) => setIsMobile(event.matches);
    mobileWidthMediaQueryList.addEventListener("change", handleMobileWidthChange);
    return () => mobileWidthMediaQueryList.removeEventListener("change", handleMobileWidthChange);
  }, []);

  useEffect(() => {
    if (!widgetRef.current || !apiKey || !cityId) {
      return
    }
    setLoading(true);
    window.myWidgetParam = [{
      id: widgetType,
      cityid: cityId,
      appid: apiKey,
      units:'imperial',
      containerid: containerId
    }]

    let cleanedUp = false;

    const insertWidgetScript = () => {
      if (cleanedUp) {
        return;
      }
      widgetScriptRef.current = insertScript({
        parentNode: widgetRef.current,
        src: OPEN_WEATHER_MAP_WIDGET_SCRIPT_SRC,
        callback: () => setLoading(false)
      });
    }

    // widget script needs to be inserted after myWidgetParam is defined
    // and after openweathermap's d3.js script is loaded
    // d3 script only needs to be added to the dom once, but widget script needs
    // to be added on every render
    if (document.getElementById('openweathermap-d3-script')) {
      insertWidgetScript();
    } else {
      insertScript({
        parentNode: document.getElementsByTagName('head')[0],
        src: OPEN_WEATHER_MAP_D3_SCRIPT_SRC,
        id: 'openweathermap-d3-script',
        callback: insertWidgetScript
      });
    }

    return () => {
      cleanedUp = true;
      widgetScriptRef.current?.remove();
      widgetScriptRef.current = null;
    };
  }, [apiKey, cityId, containerId, widgetType]);

  return (
    <div key={`${cityId}-${widgetType}`} ref={widgetRef} className="flex justify-center" data-testid={dataTestId}>
      {loading && <LoadingSpinner data-testid={`${dataTestId}-spinner`} /> }
      <div id={containerId} className={loading ? 'hidden' : ''} data-testid={`${dataTestId}-container`}></div>
    </div>
  );

}

export default OpenWeatherMapWeatherWidget;
